import { notification } from "antd"
import { Drawer, Flex, Icon } from "components/common"
import WhatsappCallButton from "features/clients/components/whatsapp-call-button"
import { useLazyGetClientStaffChatQuery, useLazyGetStaffsWhoContactedClientQuery } from "features/clients/detail/api"
import { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from "react"
import { Scrollbars } from "react-custom-scrollbars-2"
import { useTranslation } from "react-i18next"
import { PAGE_SUBSECTIONS } from "utils/gtm/gtmEventsData"
import getPrivilege from "utils/privileges/privileges"
import ChatBody from "./components/ChatBody"
import ChatFooterNotice from "./components/ChatFooterNotice"
import ChatHeader from "./components/ChatHeader"
import styles from "./index.module.less"
import StaffList from "./components/StaffList"
import WhatsappChatInsights from "./components/whatsappChatInsights/whatsappChatInsights"

const DEFAULT_STAFF_PAGE_LIMIT = 30
const DEFAULT_CHAT_PAGE_LIMIT = 40
const SCROLL_TRIGGER_THRESHOLD = 400

const STAFF_PARAMS = {
  page: 0,
  pageLimit: DEFAULT_STAFF_PAGE_LIMIT,
  sortBy: "lastMessageTimestamp",
  sortOrder: "desc",
}

const CHAT_PARAMS = {
  page: 0,
  pageLimit: DEFAULT_CHAT_PAGE_LIMIT,
  sortBy: "messageTimestamp",
  sortOrder: "desc",
}

const WhatsAppChat = forwardRef(({ client, sharingInfo }, ref) => {
  const { t } = useTranslation()
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [selectedStaff, setSelectedStaff] = useState(null)
  const [chatMessages, setChatMessages] = useState([])
  const [staffList, setStaffList] = useState([])
  const [chatHeaderInfo, setChatHeaderInfo] = useState(null)
  const scrollbarRef = useRef(null)
  const scrollAdjustRef = useRef({ oldScrollTop: 0, oldScrollHeight: 0, pending: false })
  const lastScrollTopRef = useRef(0)
  const hasMoreMessages = useRef(true)
  const totalMessages = useRef(0)
  const totalStaffs = useRef(0)
  const chatInsightsDrawerRef = useRef()
  const isInitialLoadRef = useRef(true)
  const isAutoScrollingRef = useRef(false)
  const [getStaffs, { isFetching: isFetchingStaff }] = useLazyGetStaffsWhoContactedClientQuery()
  const [getMessages, { isFetching: isFetchingChat }] = useLazyGetClientStaffChatQuery()
  const [currentStaffPage, setCurrentStaffPage] = useState(0)
  const [currentChatPage, setCurrentChatPage] = useState(0)
  const buildStaffParams = (page = currentStaffPage) => ({
    ...STAFF_PARAMS,
    clientIds: client?.id,
    page,
  })

  const buildChatParams = (chatId, page = currentChatPage) => ({
    ...CHAT_PARAMS,
    chatId,
    page,
  })

  useImperativeHandle(ref, () => ({
    setVisible: () => setIsDrawerOpen(true),
  }))

  const scrollToBottom = () => {
    isAutoScrollingRef.current = true
    setTimeout(() => {
      try {
        if (scrollbarRef.current?.scrollToBottom) {
          scrollbarRef.current.scrollToBottom()
        }
      } catch (scrollError) {
        console.warn("Error scrolling to bottom:", scrollError)
      } finally {
        setTimeout(() => {
          isAutoScrollingRef.current = false
        }, 150)
      }
    }, 100)
  }

  const loadInitialMessagesForSelectedStaff = async chatId => {
    try {
      setCurrentChatPage(0)
      const messageParams = buildChatParams(chatId, 0)
      const resp = await getMessages(messageParams).unwrap()
      totalMessages.current = resp?.count || 0
      const messageRows = Array.isArray(resp?.rows) ? resp.rows : []
      setChatHeaderInfo(resp?.header)
      setChatMessages(messageRows)
      scrollToBottom()
      return messageRows
    } catch (error) {
      notification.error({
        message: error?.message || t("Failed to load messages"),
      })
      throw error
    }
  }

  const loadStaffList = async () => {
    try {
      setCurrentStaffPage(0)
      const staffParams = buildStaffParams(0)
      const resp = await getStaffs(staffParams).unwrap()
      const staffRows = Array.isArray(resp?.rows) ? resp.rows : []
      if (staffRows.length > 0) {
        const firstStaff = staffRows[0]
        await loadInitialMessagesForSelectedStaff(firstStaff?.id)
        setSelectedStaff(firstStaff)
        setStaffList(staffRows)
        totalStaffs.current = resp?.count || 0
        isInitialLoadRef.current = false
      } else {
        isInitialLoadRef.current = false
      }
      return staffRows
    } catch (error) {
      notification.error({
        message: error?.message || t("Failed to load staff list"),
      })
      throw error
    }
  }

  useEffect(() => {
    if (!isDrawerOpen) return
    const initializeData = async () => {
      await loadStaffList()
    }
    initializeData()
  }, [isDrawerOpen])

  useEffect(() => {
    hasMoreMessages.current = chatMessages?.length >= totalMessages.current ? false : true
  }, [chatMessages])

  useLayoutEffect(() => {
    try {
      const scrollbar = scrollbarRef.current
      const { oldScrollHeight, oldScrollTop, pending } = scrollAdjustRef.current

      if (scrollbar && pending && typeof scrollbar.getScrollHeight === "function") {
        const newScrollHeight = scrollbar.getScrollHeight()
        if (typeof scrollbar.scrollTop === "function") {
          scrollbar.scrollTop(oldScrollTop + (newScrollHeight - oldScrollHeight))
        }
        scrollAdjustRef.current.pending = false
      }
    } catch (error) {
      console.warn("Error adjusting scroll position:", error)
      scrollAdjustRef.current.pending = false
    }
  }, [chatMessages])

  const handleLoadMoreMessages = async () => {
    const scrollbar = scrollbarRef.current
    if (!scrollbar || !hasMoreMessages.current || !selectedStaff?.id) return

    scrollAdjustRef.current = {
      oldScrollTop: scrollbar.getScrollTop(),
      oldScrollHeight: scrollbar.getScrollHeight(),
      pending: true,
    }

    const nextPage = currentChatPage + 1
    const messageParams = buildChatParams(selectedStaff.id, nextPage)

    try {
      const resp = await getMessages(messageParams).unwrap()
      setCurrentChatPage(nextPage)

      const newMessages = Array.isArray(resp?.rows) ? resp.rows : []
      setChatMessages(prev => {
        try {
          if (!Array.isArray(prev)) prev = []
          if (!Array.isArray(newMessages)) return prev

          const existingIds = new Set(prev.map(msg => msg?.id).filter(Boolean))
          const uniqueNewMessages = newMessages.filter(msg => msg?.id && !existingIds.has(msg.id))

          return [...uniqueNewMessages, ...prev]
        } catch (error) {
          console.warn("Error merging messages:", error)
          return prev
        }
      })
    } catch (error) {
      console.warn("Error loading more messages:", error)
      notification.error({
        message: error?.message || t("Failed to load more messages"),
      })
    }
  }

  const handleScroll = ({ scrollTop }) => {
    if (isAutoScrollingRef.current) {
      lastScrollTopRef.current = scrollTop
      return
    }

    if (scrollTop < lastScrollTopRef.current && scrollTop < SCROLL_TRIGGER_THRESHOLD) {
      handleLoadMoreMessages()
    }
    lastScrollTopRef.current = scrollTop
  }

  const handleLoadMoreStaff = async () => {
    const nextPage = currentStaffPage + 1
    const staffParams = buildStaffParams(nextPage)
    try {
      const resp = await getStaffs(staffParams).unwrap()
      const newStaffRows = Array.isArray(resp?.rows) ? resp.rows : []
      setStaffList(prev => {
        const existingList = Array.isArray(prev) ? prev : []
        return [...existingList, ...newStaffRows]
      })
      setCurrentStaffPage(nextPage)
    } catch (error) {
      notification.error({
        message: error?.message || t("Failed to load more staff"),
      })
    }
  }

  const resetToDefault = () => {
    setIsDrawerOpen(false)
    setSelectedStaff(null)
    setChatMessages([])
    setStaffList([])
    setCurrentStaffPage(0)
    setCurrentChatPage(0)
    isInitialLoadRef.current = true
    isAutoScrollingRef.current = false
    scrollAdjustRef.current = { oldScrollTop: 0, oldScrollHeight: 0, pending: false }
    lastScrollTopRef.current = 0
    hasMoreMessages.current = true
    totalMessages.current = 0
    totalStaffs.current = 0
  }

  const handleStaffSelection = staff => {
    if (staff?.id === selectedStaff?.id) return
    setSelectedStaff(staff)
    setChatMessages([])
    const loadSelectedStaffMessages = async () => {
      await loadInitialMessagesForSelectedStaff(staff.id)
    }
    loadSelectedStaffMessages()
  }
  return (
    <>
      <Drawer
        title={t("Chats")}
        placement="right"
        closable={false}
        open={isDrawerOpen}
        onClose={resetToDefault}
        width={1400}
        className={styles.whatsappDrawer}
        footer={null}
        extra={
          <Icon icon="IoMdClose" shape="circle" className="pointer" onClick={resetToDefault} />
        }
        bodyStyle={{ padding: 0 }}
      >
        <Flex className={styles.chatMainContainer} justify="space-between" align="stretch">
          <StaffList
            staffList={staffList}
            selectedStaff={selectedStaff}
            onSelectStaff={(staff) => handleStaffSelection(staff)}
            handleLoadButton={handleLoadMoreStaff}
            isLoadingStaffList={isFetchingStaff}
            totalStaffCount={totalStaffs.current}
            client={client}
            sharingInfo={sharingInfo}
          />
          <Flex vertical className={styles.chatContainer}>
            {selectedStaff &&

              <ChatHeader
                selectedStaff={selectedStaff}
                client={client}
                isLoading={isFetchingChat && !chatMessages?.length}
                chatHeaderInfo={chatHeaderInfo}
                onInsightsClick={() => {
                  chatInsightsDrawerRef?.current?.showDrawer()
                }}
              />
            }
            <div className={styles.chatBodyWrapper}>
              <Scrollbars ref={scrollbarRef} onScrollFrame={handleScroll} className={styles.chatScrollWrapper} autoHide>
                <ChatBody
                  chatMessages={chatMessages}
                  isLoading={isFetchingChat && !chatMessages?.length}
                  isLoadingMore={isFetchingChat && chatMessages?.length > 0}
                  client={client}
                  selectedStaff={selectedStaff}
                />
              </Scrollbars>
            </div>
          </Flex>
        </Flex>
      </Drawer>
      <WhatsappChatInsights ref={chatInsightsDrawerRef} selectedStaff={selectedStaff} client={client} />
    </>
  )
})

export default WhatsAppChat
//